/**
 * Moteur de sélection (cahier §9, TODO phase 13, ADR-030).
 *
 * Partagé entre l'API (contrôles, calcul en phase 15) et le portail admin
 * (configurateur, aperçu). AUCUNE règle officielle n'est codée en dur (ADR-011) :
 * tout vient de la configuration versionnée d'un concours. Les fonctions de
 * calcul sont PURES et testées indépendamment des données.
 */
/* ------------------------------------------------------------------ */
/* Étapes                                                              */
/* ------------------------------------------------------------------ */
export const STAGE_KINDS = {
    written: 'Épreuve écrite',
    physical: 'Épreuve physique',
    oral: 'Entretien oral',
    administrative: 'Vérification administrative',
    medical: 'Visite médicale',
    other: 'Autre étape',
};
export const STAGE_POPULATIONS = {
    admissible: 'Tous les dossiers recevables',
    previous: 'Admis de l’étape précédente',
};
export const STAGE_STATUSES = {
    draft: 'En préparation',
    grading: 'Notation ouverte (barèmes verrouillés)',
    closed: 'Clôturée',
};
export const STAGE_PUBLICATIONS = {
    after_approval: 'Après approbation par l’autorité habilitée',
    manual: 'Publication manuelle décidée par le responsable',
};
/** Proposition initiale, modifiable par concours (cahier §9.1). */
export const DEFAULT_STATUS_OPTIONS = [
    { code: 'apte', label: 'Apte', passes: true },
    { code: 'non_apte', label: 'Non apte', passes: false },
    { code: 'present', label: 'Présent', passes: true },
    { code: 'absent', label: 'Absent', passes: false },
    { code: 'valide', label: 'Validé', passes: true },
];
/* ------------------------------------------------------------------ */
/* Disciplines                                                         */
/* ------------------------------------------------------------------ */
export const GRADING_TYPES = {
    numeric: 'Note numérique',
    performance: 'Barème de performance',
    status: 'Statut (sans note)',
};
export const MULTI_EVALUATOR_RULES = {
    average: 'Moyenne des évaluateurs',
    max_gap: 'Moyenne, 3e correction si l’écart dépasse le seuil',
};
/** Absence : jamais assimilée à zéro par défaut (cahier §9.6) — choix explicite. */
export const ABSENCE_POLICIES = {
    eliminatory: 'Éliminatoire',
    zero: 'Comptée zéro',
    excluded: 'Discipline non comptée',
};
export const EXEMPTION_POLICIES = {
    excluded: 'Discipline non comptée (coefficient retiré)',
    zero: 'Comptée zéro',
};
export const MISSING_POLICIES = {
    block: 'Bloque le calcul (note à compléter)',
    zero: 'Comptée zéro',
};
export const PERFORMANCE_UNITS = {
    seconds: 'Secondes',
    meters: 'Mètres',
    repetitions: 'Répétitions',
    points: 'Points',
};
export const PERFORMANCE_DIRECTIONS = {
    lower_better: 'Plus petit = meilleur (temps)',
    higher_better: 'Plus grand = meilleur (distance, répétitions)',
};
/* ------------------------------------------------------------------ */
/* Règles de passage (versionnées, validées avant tout calcul)         */
/* ------------------------------------------------------------------ */
export const COMPUTATION_METHODS = {
    weighted_average: 'Moyenne pondérée',
    weighted_sum: 'Somme pondérée',
};
export const PASS_MODES = {
    threshold: 'Seuil minimal',
    quota: 'Quota : les mieux classés',
    threshold_quota: 'Seuil et quota combinés',
    status: 'Statuts (étape non notée)',
    manual: 'Décision manuelle motivée',
};
export const TIE_BREAKER_LABELS = {
    discipline: 'Meilleure note dans une discipline',
    age: 'Âge',
};
export const RULE_VERSION_STATUSES = {
    draft: 'Brouillon',
    validated: 'Validée (applicable)',
    superseded: 'Remplacée',
};
export const EMPTY_PASS_RULE = {
    method: 'weighted_average',
    mode: 'threshold',
    threshold: null,
    quota: null,
    eliminatoryScore: null,
    tieBreakers: [],
    supplementary: { enabled: false, size: null },
    byCategory: false,
};
/** Cohérence d'une discipline (avant enregistrement et au verrouillage). */
export function validateDiscipline(d) {
    const issues = [];
    if (d.grading !== 'status') {
        if (!(d.maxScore > d.minScore))
            issues.push({
                field: 'maxScore',
                message: 'La note maximale doit dépasser la note minimale.',
            });
        if (!(d.coefficient > 0))
            issues.push({ field: 'coefficient', message: 'Le coefficient doit être positif.' });
        if (!(d.precision > 0) || d.precision > d.maxScore - d.minScore)
            issues.push({
                field: 'precision',
                message: 'Précision de saisie incohérente avec le barème.',
            });
        if (d.eliminatoryThreshold !== null &&
            (d.eliminatoryThreshold < d.minScore || d.eliminatoryThreshold > d.maxScore))
            issues.push({
                field: 'eliminatoryThreshold',
                message: 'Le seuil éliminatoire doit être compris dans le barème.',
            });
    }
    if (d.evaluators > 1 && d.multiRule === 'max_gap' && !(d.maxGap && d.maxGap > 0))
        issues.push({ field: 'maxGap', message: 'Indiquez l’écart déclenchant une 3e correction.' });
    if (d.multiRule === 'max_gap' && d.evaluators < 2)
        issues.push({
            field: 'multiRule',
            message: 'La 3e correction suppose au moins deux évaluateurs.',
        });
    if (d.grading === 'performance') {
        const rows = d.scale?.rows ?? [];
        if (rows.length < 2)
            issues.push({
                field: 'scale',
                message: 'Le barème de performance compte au moins deux lignes.',
            });
        for (const r of rows)
            if (r.score < d.minScore || r.score > d.maxScore) {
                issues.push({
                    field: 'scale',
                    message: 'Une note du barème sort de l’intervalle de notation.',
                });
                break;
            }
        for (const gender of [null, 'F', 'M']) {
            const perfs = rows.filter((r) => r.gender === gender).map((r) => r.performance);
            if (new Set(perfs).size !== perfs.length) {
                issues.push({ field: 'scale', message: 'Deux lignes du barème ont la même performance.' });
                break;
            }
        }
    }
    if (d.grading === 'status') {
        const s = d.statuses ?? [];
        if (s.length < 2)
            issues.push({ field: 'statuses', message: 'Au moins deux statuts.' });
        if (!s.some((x) => x.passes))
            issues.push({
                field: 'statuses',
                message: 'Au moins un statut doit permettre de poursuivre.',
            });
    }
    return issues;
}
/** Une règle de passage est-elle applicable à cette étape ? (avant validation) */
export function validatePassRule(rule, stage) {
    const issues = [];
    const numeric = stage.disciplines.filter((d) => d.grading !== 'status');
    if (!stage.graded) {
        if (!['status', 'manual'].includes(rule.mode))
            issues.push({
                field: 'mode',
                message: 'Une étape non notée se décide par statut ou par décision manuelle.',
            });
        return issues;
    }
    if (rule.mode === 'status')
        issues.push({
            field: 'mode',
            message: 'Le mode « statuts » est réservé aux étapes non notées.',
        });
    if (!numeric.length)
        issues.push({ field: 'disciplines', message: 'Aucune discipline notée dans cette étape.' });
    const max = rule.method === 'weighted_sum'
        ? numeric.reduce((s, d) => s + d.maxScore * d.coefficient, 0)
        : Math.max(0, ...numeric.map((d) => d.maxScore));
    if (['threshold', 'threshold_quota'].includes(rule.mode)) {
        if (rule.threshold === null)
            issues.push({ field: 'threshold', message: 'Seuil de passage requis.' });
        else if (rule.threshold < 0 || (numeric.length && rule.threshold > max))
            issues.push({ field: 'threshold', message: `Le seuil doit être compris entre 0 et ${max}.` });
    }
    if (['quota', 'threshold_quota'].includes(rule.mode)) {
        if (!rule.quota || rule.quota < 1)
            issues.push({ field: 'quota', message: 'Nombre de places requis.' });
        else if (stage.seats && rule.quota > stage.seats)
            issues.push({ field: 'quota', message: 'Le quota dépasse le nombre de places de l’étape.' });
    }
    if (rule.eliminatoryScore !== null && (rule.eliminatoryScore < 0 || rule.eliminatoryScore > max))
        issues.push({ field: 'eliminatoryScore', message: 'Note éliminatoire hors du barème.' });
    if (rule.threshold !== null &&
        rule.eliminatoryScore !== null &&
        rule.eliminatoryScore > rule.threshold)
        issues.push({
            field: 'eliminatoryScore',
            message: 'La note éliminatoire ne peut pas dépasser le seuil de passage.',
        });
    const codes = new Set(numeric.map((d) => d.code));
    rule.tieBreakers.forEach((t, i) => {
        if (t.kind === 'discipline' && !codes.has(t.discipline))
            issues.push({
                field: `tieBreakers.${i}`,
                message: 'Départage sur une discipline absente de l’étape.',
            });
    });
    if (rule.supplementary.enabled && (!rule.supplementary.size || rule.supplementary.size < 1))
        issues.push({ field: 'supplementary', message: 'Taille de la liste complémentaire requise.' });
    if (rule.supplementary.enabled && rule.mode === 'manual')
        issues.push({
            field: 'supplementary',
            message: 'Une liste complémentaire suppose un classement (seuil ou quota).',
        });
    return issues;
}
/* ------------------------------------------------------------------ */
/* Calcul (pur)                                                        */
/* ------------------------------------------------------------------ */
/** Arrondi au pas de saisie (ex. 0,25), sans erreur de virgule flottante. */
export function roundTo(value, precision) {
    const factor = 1 / precision;
    return Math.round(value * factor + Number.EPSILON) / factor;
}
/**
 * Performance → note selon le barème. Lignes propres au sexe du candidat si
 * elles existent, sinon lignes communes. Sous le plus bas niveau : note minimale.
 */
export function scorePerformance(scale, value, gender, minScore = 0) {
    const own = scale.rows.filter((r) => r.gender === gender && gender !== null);
    const rows = own.length ? own : scale.rows.filter((r) => r.gender === null);
    if (scale.direction === 'lower_better') {
        // Temps : la meilleure ligne dont le temps est ≥ à la performance.
        const ok = rows
            .filter((r) => value <= r.performance)
            .sort((a, b) => a.performance - b.performance);
        return ok[0]?.score ?? minScore;
    }
    const ok = rows
        .filter((r) => value >= r.performance)
        .sort((a, b) => b.performance - a.performance);
    return ok[0]?.score ?? minScore;
}
/** Agrégation multi-évaluateurs ; `null` = 3e correction attendue. */
export function aggregateCorrections(values, d) {
    if (!values.length)
        return null;
    if (values.length === 1 || d.evaluators === 1)
        return values[0];
    if (d.multiRule === 'max_gap' && values.length === 2) {
        if (Math.abs(values[0] - values[1]) > (d.maxGap ?? Infinity))
            return null;
    }
    if (values.length >= 3) {
        // 3e correction : moyenne des deux notes les plus proches.
        const sorted = [...values].sort((a, b) => a - b);
        const low = [sorted[0], sorted[1]];
        const high = [sorted[sorted.length - 2], sorted[sorted.length - 1]];
        const pair = high[1] - high[0] < low[1] - low[0] ? high : low;
        return (pair[0] + pair[1]) / 2;
    }
    return values.reduce((a, b) => a + b, 0) / values.length;
}
/** Note d'étape d'un candidat, selon la configuration explicite de chaque discipline. */
export function computeCandidate(disciplines, marks, rule, gender = null) {
    const out = {
        score: null,
        eliminated: false,
        incomplete: false,
        reasons: [],
        disciplines: [],
    };
    let weighted = 0;
    let weights = 0;
    for (const d of disciplines) {
        const mark = marks[d.code];
        const push = (score, counted, note) => out.disciplines.push({ code: d.code, score, counted, note });
        if (d.grading === 'status') {
            const option = d.statuses?.find((s) => s.code === mark?.statusCode);
            if (!option) {
                out.incomplete = true;
                out.reasons.push(`${d.label} : statut non renseigné.`);
                push(null, false, 'manquant');
            }
            else {
                if (!option.passes) {
                    out.eliminated = true;
                    out.reasons.push(`${d.label} : ${option.label}.`);
                }
                push(null, false, option.label);
            }
            continue;
        }
        let score = null;
        if (!mark || (mark.status === 'present' && !mark.values?.length)) {
            if (d.missing === 'zero')
                score = d.minScore;
            else {
                out.incomplete = true;
                out.reasons.push(`${d.label} : note manquante.`);
                push(null, false, 'manquante');
                continue;
            }
        }
        else if (mark.status === 'absent') {
            if (d.absence === 'eliminatory') {
                out.eliminated = true;
                out.reasons.push(`${d.label} : absence éliminatoire.`);
                push(null, false, 'absent');
                continue;
            }
            if (d.absence === 'excluded') {
                push(null, false, 'absent (non comptée)');
                continue;
            }
            score = d.minScore;
        }
        else if (mark.status === 'exempt') {
            if (d.exemption === 'excluded') {
                push(null, false, 'dispensé (non comptée)');
                continue;
            }
            score = d.minScore;
        }
        else {
            const values = d.grading === 'performance' && d.scale
                ? mark.values.map((v) => scorePerformance(d.scale, v, gender, d.minScore))
                : mark.values;
            const aggregated = aggregateCorrections(values, d);
            if (aggregated === null) {
                out.incomplete = true;
                out.reasons.push(`${d.label} : écart entre correcteurs, 3e correction attendue.`);
                push(null, false, '3e correction');
                continue;
            }
            score = roundTo(Math.min(d.maxScore, Math.max(d.minScore, aggregated)), d.precision);
        }
        if (d.eliminatoryThreshold !== null && score < d.eliminatoryThreshold) {
            out.eliminated = true;
            out.reasons.push(`${d.label} : ${score} < seuil éliminatoire ${d.eliminatoryThreshold}.`);
        }
        else if (!d.compensable &&
            rule.method === 'weighted_average' &&
            rule.threshold !== null &&
            score < rule.threshold) {
            out.eliminated = true;
            out.reasons.push(`${d.label} : non compensable, ${score} < ${rule.threshold}.`);
        }
        weighted += score * d.coefficient;
        weights += d.coefficient;
        push(score, true, null);
    }
    if (!out.incomplete && weights > 0)
        out.score = roundTo(rule.method === 'weighted_sum' ? weighted : weighted / weights, 0.01);
    if (out.score !== null && rule.eliminatoryScore !== null && out.score < rule.eliminatoryScore) {
        out.eliminated = true;
        out.reasons.push(`Note ${out.score} < note éliminatoire ${rule.eliminatoryScore}.`);
    }
    return out;
}
export const PASS_DECISIONS = {
    passed: 'Admis',
    supplementary: 'Liste complémentaire',
    failed: 'Non admis',
    eliminated: 'Éliminé',
    incomplete: 'Incomplet',
    absent: 'Absent',
    pending: 'Décision humaine requise',
};
/**
 * Classement et décisions de passage. Les ex æquo sont départagés UNIQUEMENT
 * par les règles fournies ; à défaut, ils sont signalés (`tie`) et, s'ils
 * chevauchent la limite des places, renvoyés à une décision humaine.
 */
export function rankCandidates(inputs, rule) {
    const scoreOf = (i, code) => i.outcome.disciplines.find((d) => d.code === code)?.score ?? -Infinity;
    const compare = (a, b) => {
        const diff = (b.outcome.score ?? 0) - (a.outcome.score ?? 0);
        if (diff)
            return diff;
        for (const t of rule.tieBreakers) {
            if (t.kind === 'discipline') {
                const d = scoreOf(b, t.discipline) - scoreOf(a, t.discipline);
                if (d)
                    return d;
            }
            else if (a.birthDate && b.birthDate && a.birthDate !== b.birthDate) {
                // Plus jeune = date de naissance plus récente.
                const younger = a.birthDate > b.birthDate ? -1 : 1;
                return t.order === 'younger' ? younger : -younger;
            }
        }
        return 0;
    };
    const result = new Map();
    const eligible = inputs.filter((i) => !i.outcome.eliminated && !i.outcome.incomplete);
    for (const i of inputs)
        if (i.outcome.eliminated || i.outcome.incomplete)
            result.set(i.id, {
                id: i.id,
                rank: null,
                score: i.outcome.score,
                decision: i.outcome.incomplete ? 'incomplete' : 'eliminated',
                tie: false,
                reasons: i.outcome.reasons,
            });
    const sorted = [...eligible].sort(compare);
    let rank = 0;
    sorted.forEach((c, index) => {
        if (index === 0 || compare(sorted[index - 1], c) !== 0)
            rank = index + 1;
        const tie = (index > 0 && compare(sorted[index - 1], c) === 0) ||
            (index < sorted.length - 1 && compare(c, sorted[index + 1]) === 0);
        result.set(c.id, {
            id: c.id,
            rank,
            score: c.outcome.score,
            decision: 'failed',
            tie,
            reasons: [],
        });
    });
    const meets = (r) => rule.threshold === null || (r.score !== null && r.score >= rule.threshold);
    const ranked = sorted.map((c) => result.get(c.id));
    if (rule.mode === 'manual') {
        for (const r of ranked)
            r.decision = 'pending';
        return inputs.map((i) => result.get(i.id));
    }
    const pool = ranked.filter(meets);
    for (const r of ranked)
        if (!meets(r))
            r.reasons.push(`Note inférieure au seuil ${rule.threshold}.`);
    const places = rule.mode === 'quota' || rule.mode === 'threshold_quota' ? (rule.quota ?? 0) : pool.length;
    const extra = rule.supplementary.enabled ? (rule.supplementary.size ?? 0) : 0;
    const assign = (from, count, decision) => {
        const slice = pool.slice(from, from + count);
        for (const r of slice)
            r.decision = decision;
        // Égalité qui chevauche la limite : décision humaine pour tout le groupe.
        const last = slice.at(-1);
        const next = pool[from + count];
        if (last && next && last.rank === next.rank) {
            for (const r of pool.filter((x) => x.rank === last.rank)) {
                r.decision = 'pending';
                r.reasons.push('Ex æquo à la limite des places : décision humaine requise.');
            }
        }
    };
    assign(0, places, 'passed');
    if (extra) {
        const taken = pool.filter((r) => r.decision === 'passed' || r.decision === 'pending').length;
        const start = Math.max(places, taken);
        const slice = pool.slice(start, start + extra);
        for (const r of slice)
            if (r.decision === 'failed')
                r.decision = 'supplementary';
        // Égalité à la limite de la liste complémentaire : décision humaine aussi.
        const last = slice.at(-1);
        const next = pool[start + extra];
        if (last && next && last.rank === next.rank)
            for (const r of pool.filter((x) => x.rank === last.rank && x.decision !== 'passed')) {
                r.decision = 'pending';
                r.reasons.push('Ex æquo à la limite de la liste complémentaire : décision humaine requise.');
            }
    }
    for (const r of pool)
        if (r.decision === 'failed')
            r.reasons.push('Classé au-delà des places disponibles.');
    return inputs.map((i) => result.get(i.id));
}
//# sourceMappingURL=selection.js.map